import type { Filter } from 'contexts/preferences/listing-params/types';
import { AlertFrequency } from './value-objects/alert-frequency';
import { summarizeFilter } from './search-summary';
import type { SearchSummary } from './search-summary';
import { SavedSearchPolicy } from './policies/saved-search-policy';
import type { PersistedSavedSearch, SavedSearchUpdateAttributes } from './types';

/**
 * SavedSearchRecord
 *
 * A persisted saved search (identity = `id`). Built from a data-layer record via
 * `from`, supports immutable edits, and produces the update payload.
 *
 * Named `SavedSearchRecord` rather than `SavedSearch` so it coexists with the
 * data-layer `SavedSearch` DTO during the migration without a name clash.
 *
 * The `filter` — including `homeType` — is passed through untouched: home-type
 * backward-compat is already applied by the data layer on both read and write, so
 * the domain must not re-derive it (doing so would double-apply).
 *
 * `listingsCount` and `imageUrl` are server-computed, read-only attributes. They
 * are not sent back on update.
 *
 * Immutable.
 */
export class SavedSearchRecord {
  readonly #id: string;
  readonly #filter: Filter;
  readonly #description: string;
  readonly #frequency: AlertFrequency;
  readonly #source: string;
  readonly #listingsCount: number | null;
  readonly #newListingsCount: number | null;
  readonly #imageUrl: string | null;
  readonly #createdAt?: Date;
  readonly #updatedOn?: Date;

  private constructor(params: {
    id: string;
    filter: Filter;
    description: string;
    frequency: AlertFrequency;
    source: string;
    listingsCount: number | null;
    newListingsCount: number | null;
    imageUrl: string | null;
    createdAt?: Date;
    updatedOn?: Date;
  }) {
    this.#id = params.id;
    this.#filter = params.filter;
    this.#description = params.description;
    this.#frequency = params.frequency;
    this.#source = params.source;
    this.#listingsCount = params.listingsCount;
    this.#newListingsCount = params.newListingsCount;
    this.#imageUrl = params.imageUrl;
    this.#createdAt = params.createdAt;
    this.#updatedOn = params.updatedOn;
  }

  /** Builds a record from a persisted DTO. Filter is not mutated. */
  static from(raw: PersistedSavedSearch): SavedSearchRecord {
    return new SavedSearchRecord({
      id: raw.id,
      filter: raw.formParams.filter,
      description: raw.description,
      frequency: AlertFrequency.from(raw.frequency),
      source: raw.source,
      listingsCount: raw.listingsCount ?? null,
      newListingsCount: raw.newListingsCount ?? null,
      imageUrl: raw.imageUrl ?? null,
      createdAt: raw.createdAt,
      updatedOn: raw.updatedOn,
    });
  }

  get id(): string {
    return this.#id;
  }

  get filter(): Filter {
    return this.#filter;
  }

  /**
   * The meaningful, non-default facets of this search's filter — a computed fact
   * derived from `filter`. The presentation layer formats it into a display line.
   */
  get summary(): SearchSummary {
    return summarizeFilter(this.#filter);
  }

  get description(): string {
    return this.#description;
  }

  get frequency(): AlertFrequency {
    return this.#frequency;
  }

  get source(): string {
    return this.#source;
  }

  /** Number of listings currently matching this search, or null if unknown. */
  get listingsCount(): number | null {
    return this.#listingsCount;
  }

  /**
   * Listings added within the window this search's alerts cover, so "8 New"
   * means the same thing the next alert email will. Null when alerts are off,
   * since there is then no window to count over, and while the server has not
   * cached it yet.
   *
   * Server-computed and read-only, like listingsCount. Not sent back on update.
   */
  get newListingsCount(): number | null {
    return this.#newListingsCount;
  }

  /** Representative listing image for the search, or null if none. */
  get imageUrl(): string | null {
    return this.#imageUrl;
  }

  get createdAt(): Date | undefined {
    return this.#createdAt;
  }

  get updatedOn(): Date | undefined {
    return this.#updatedOn;
  }

  withFrequency(frequency: AlertFrequency): SavedSearchRecord {
    const resolved = SavedSearchPolicy.resolveFrequency(this.#filter.status, frequency);
    return new SavedSearchRecord({
      id: this.#id,
      filter: this.#filter,
      description: this.#description,
      frequency: resolved,
      source: this.#source,
      listingsCount: this.#listingsCount,
      newListingsCount: this.#newListingsCount,
      imageUrl: this.#imageUrl,
      createdAt: this.#createdAt,
      updatedOn: this.#updatedOn,
    });
  }

  withDescription(description: string): SavedSearchRecord {
    return new SavedSearchRecord({
      id: this.#id,
      filter: this.#filter,
      description,
      frequency: this.#frequency,
      source: this.#source,
      listingsCount: this.#listingsCount,
      newListingsCount: this.#newListingsCount,
      imageUrl: this.#imageUrl,
      createdAt: this.#createdAt,
      updatedOn: this.#updatedOn,
    });
  }

  toUpdatePayload(): SavedSearchUpdateAttributes {
    return {
      id: this.#id,
      description: this.#description,
      frequency: this.#frequency.seconds,
      source: this.#source,
      formParams: { filter: this.#filter },
    };
  }
}
